// Propósito: traducir a lenguaje comercial (para vendedores/operadores) los
// identificadores técnicos del embudo: labels de slots, factores del score y
// eventos del timeline. Sin esto la ficha muestra claves crudas como
// "gestor_deuda.threshold_ok" o "slot_captured / Actor: LLM".
import type { LeadEventPublic } from '@workspace/types';

const MAX_TOKEN = 128;

export const SLOT_LABELS: Record<string, string> = {
  contacto_nombre: 'Contacto',
  empresa: 'Empresa',
  contacto_cargo: 'Cargo',
  telefono: 'Teléfono',
  email: 'Email',
  pais: 'País',
  autoridad: 'Autoridad de decisión',
  dolor: 'Dolor principal',
  urgencia: 'Urgencia',
  interes_reunion: 'Interés en reunión',
  franja_horaria: 'Franja horaria',
  cuentas_mora: 'Cuentas en mora',
  tipo_deuda: 'Tipo de deuda',
  rubro: 'Rubro',
  gestion_actual: 'Gestión actual',
  equipo_cobranzas: 'Equipo de cobranzas',
  canal_contacto: 'Canal a deudores',
  sistema_origen: 'Sistema de origen',
  objetivo_principal: 'Objetivo principal',
  vehiculos: 'Vehículos',
  tipo_flota: 'Tipo de flota',
  metros_cuadrados: 'Metros cuadrados',
  envios_dia: 'Envíos por día',
};

function humanize(value: string): string {
  const clean = value.slice(0, MAX_TOKEN).replace(/_/g, ' ').trim();
  if (!clean) {
    return '';
  }
  return clean.charAt(0).toUpperCase() + clean.slice(1);
}

export function slotLabel(key: string): string {
  return SLOT_LABELS[key] ?? humanize(key.slice(0, 64));
}

// --- Factores del score ---------------------------------------------------

// Factores tipo "clave=valor" (autoridad=decisor, gestion=planillas, channel=WEB…).
const FACTOR_PREFIX_LABELS: Record<string, string> = {
  autoridad: 'Autoridad',
  gestion: 'Gestión actual',
  pais: 'País',
  channel: 'Canal',
};

// Factores sin valor asociado.
const FACTOR_EXACT_LABELS: Record<string, string> = {
  pain_match: 'Coincide con la necesidad detectada',
  urgency_declared: 'Urgencia declarada',
  threshold_ok: 'Volumen suficiente',
  threshold_open: 'Volumen suficiente',
  threshold_below: 'Volumen parcial',
};

function formatKeyValueFactor(token: string, separator: number): string {
  const key = token.slice(0, separator);
  const value = token.slice(separator + 1);
  const label = FACTOR_PREFIX_LABELS[key] ?? humanize(key);
  return `${label}: ${humanize(value)}`;
}

/**
 * Convierte un factor crudo del scoring en una etiqueta comercial. Descarta el
 * prefijo del vertical (p. ej. "gestor_deuda.threshold_ok" → "Volumen suficiente")
 * para no exponer el nombre interno del producto en el embudo de leads.
 */
export function formatScoreFactor(raw: string): string {
  const token = raw.slice(0, MAX_TOKEN).trim();
  const separator = token.indexOf('=');
  if (separator > 0) {
    return formatKeyValueFactor(token, separator);
  }
  const dot = token.indexOf('.');
  const suffix = dot >= 0 ? token.slice(dot + 1) : token;
  return FACTOR_EXACT_LABELS[suffix] ?? humanize(suffix);
}

// --- Timeline -------------------------------------------------------------

const EVENT_LABELS: Record<string, string> = {
  lead_created: 'Lead creado',
  slot_captured: 'Dato capturado',
  slot_rejected: 'Dato descartado',
  vertical_recast: 'Cambio de interés',
  insufficient: 'Datos insuficientes',
  handoff: 'Derivado a comercial',
  owner_assigned: 'Asignado a un vendedor',
  summary_generated: 'Resumen comercial generado',
};

const ACTOR_LABELS: Record<string, string> = {
  ENGINE: 'Sistema',
  LLM: 'Asistente IA',
  HUMAN: 'Persona',
};

export function formatEventActor(actor: string): string {
  return ACTOR_LABELS[actor] ?? humanize(actor);
}

export function formatEventTitle(event: LeadEventPublic): string {
  const base = EVENT_LABELS[event.eventType] ?? humanize(event.eventType);
  const slotKey = typeof event.output?.slotKey === 'string' ? event.output.slotKey : null;
  if (slotKey && (event.eventType === 'slot_captured' || event.eventType === 'slot_rejected')) {
    return `${base}: ${slotLabel(slotKey)}`;
  }
  return base;
}
